/**
 * Redux Store 配置
 * Offshore Green Bond Express离岸绿色债券直通车统一状态管理
 */

import { configureStore, Middleware } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { apiSlice } from './api/apiSlice';
import { issuerProjectApi } from './api/issuerProjectApi';
import { detailedAssessmentApi } from './api/detailedAssessmentApi';
import { projectManagementApi } from './api/projectManagementApi';
import { spoApi } from './api/spoApi';
import authReducer from './slices/authSlice';
import questionnaireReducer from './slices/questionnaireSlice';
import projectReducer from './slices/projectSlice';
import screeningReducer from './slices/screeningSlice';

// 创建缺少的slice reducers (临时实现)
const stageReducer = (
  state = {
    currentStage: 'initial',
    pendingTransition: null,
    isTransitioning: false,
  },
  action: any
) => state;

const companyReducer = (
  state = {
    profile: null,
  },
  action: any
) => state;

const uiReducer = (
  state = {
    notifications: [],
  },
  action: any
) => state;

// 创建RTK Query缓存清除middleware
const rtkQueryCacheResetMiddleware: Middleware =
  store => next => (action: any) => {
    const result = next(action);

    // 监听logout action
    if (action.type === 'auth/logout') {
      console.log('检测到logout action，清除RTK Query缓存');

      // 清除所有API缓存
      store.dispatch(apiSlice.util.resetApiState());
      store.dispatch(issuerProjectApi.util.resetApiState());
      store.dispatch(detailedAssessmentApi.util.resetApiState());
      store.dispatch(projectManagementApi.util.resetApiState());
      store.dispatch(spoApi.util.resetApiState());

      console.log('RTK Query缓存清除完成');
    }

    // 监听登录成功action
    if (action.type === 'auth/loginSuccess') {
      console.log('检测到登录成功，清除RTK Query缓存以确保数据隔离');

      // 清除所有API缓存，确保新用户看到正确的数据
      store.dispatch(apiSlice.util.resetApiState());
      store.dispatch(issuerProjectApi.util.resetApiState());
      store.dispatch(detailedAssessmentApi.util.resetApiState());
      store.dispatch(projectManagementApi.util.resetApiState());
      store.dispatch(spoApi.util.resetApiState());

      console.log('登录成功后RTK Query缓存清除完成');
    }

    return result;
  };

// 配置Redux Store
export const store = configureStore({
  reducer: {
    // API slice - RTK Query
    api: apiSlice.reducer,
    issuerProjectApi: issuerProjectApi.reducer,
    detailedAssessmentApi: detailedAssessmentApi.reducer,
    projectManagementApi: projectManagementApi.reducer,
    spoApi: spoApi.reducer,

    // 业务状态slices
    auth: authReducer,
    questionnaire: questionnaireReducer,
    project: projectReducer,
    screening: screeningReducer,

    // 临时添加的slices
    stage: stageReducer,
    company: companyReducer,
    ui: uiReducer,
  },

  // 中间件配置
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: {
        // 忽略RTK Query的action类型
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
        // 忽略非序列化的路径
        ignoredPaths: [
          'api.queries',
          'api.mutations',
          'issuerProjectApi.queries',
          'issuerProjectApi.mutations',
          'detailedAssessmentApi.queries',
          'detailedAssessmentApi.mutations',
          'projectManagementApi.queries',
          'projectManagementApi.mutations',
          'spoApi.queries',
          'spoApi.mutations',
        ],
      },
    }).concat(
      apiSlice.middleware,
      issuerProjectApi.middleware,
      detailedAssessmentApi.middleware,
      projectManagementApi.middleware,
      spoApi.middleware,
      rtkQueryCacheResetMiddleware // 添加缓存清除middleware
    ),

  // 开发环境启用DevTools
  devTools: process.env.NODE_ENV !== 'production',
});

// 设置监听器用于重新获取和缓存管理
setupListeners(store.dispatch);

// TypeScript类型定义
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// 类型化的hooks
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
